import { useMemo } from 'react'
import Link from 'next/link'

import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'

import { Header } from 'app/KinerjaShared/components/Header'
import { getUserDisplayName } from 'utils/user'
import { useAuth } from 'utils/auth'
import {
  SectionCard,
  SectionCardHeader,
  SectionCardBody,
} from 'app/KinerjaShared/components/SectionCard'
import CircularProgress from 'app/KinerjaShared/components/CircularProgress'
import { PENGELOLAAN_KINERJA_GPK_TIM_KINERJA_PATH } from 'app/KinerjaShared/constants/path'
import { useApiGetPelaksanaanList } from 'api/kinerja/gpk/pelaksanaan'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'

import type { MultiPeriodPayload } from 'types/kinerja'

type HubPemantauanKinerjaProps = {
  periodParams?: MultiPeriodPayload
}

export function HubPemantauanKinerja({
  periodParams,
}: HubPemantauanKinerjaProps) {
  const { periodNo, periodYear } = usePeriodContext()
  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: PENGELOLAAN_KINERJA_GPK_TIM_KINERJA_PATH,
  })

  //-----------------------------------------
  // Resolve period params
  // -----------------------------------------
  const resolvedPeriodParams = useMemo(() => {
    if (periodParams) return periodParams

    // Default with current period
    return {
      skpPeriod: periodNo,
      skpYear: periodYear,
    }
  }, [periodParams, periodNo, periodYear])

  // -----------------------------------------
  // Get pelaksanaan list
  // -----------------------------------------
  const { isLoading: isPelaksanaanListLoading, data: pelaksanaanListData } =
    useApiGetPelaksanaanList({
      params: {
        skpPeriod: resolvedPeriodParams.skpPeriod,
        skpYear: resolvedPeriodParams.skpYear,
      },
      options: {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    })

  // -----------------------------------------
  // Resolve greeting message
  // -----------------------------------------
  const { session } = useAuth()
  const greetingMessage = useMemo(() => {
    const userName = session?.user?.name

    if (!userName) return 'Selamat datang!'

    const displayName = getUserDisplayName(userName)
    return `Selamat datang, ${displayName}!`
  }, [session?.user?.name])

  return (
    <main className="pt-12 px-8 pb-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex justify-between items-center">
          <Header
            title="Pemantauan Kinerja dan Rekomendasi Predikat Kinerja"
            className="mb-9"
            onBack={handleBack}
          />
        </div>

        <SectionCard>
          <SectionCardHeader variant="gradient-blue">
            <Text
              className="overflow-wrap-anywhere line-clamp-1 mb-1"
              color="inherit"
              variant="heading-lg"
              as="h3"
            >
              {greetingMessage}
            </Text>
            <Text color="inherit">
              Pemantauan yang Anda lakukan merupakan aspek penting dalam
              peningkatan kinerja satuan pendidikan Anda.
            </Text>
          </SectionCardHeader>
          <SectionCardBody className="py-6 px-8 space-y-6">
            <div className="border border-subdued rounded py-6 pl-6 pr-8 space-x-6 flex items-center">
              <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center">
                {isPelaksanaanListLoading ? (
                  <Shimmer width={80} height={80} className="!rounded-full" />
                ) : (
                  <CircularProgress
                    value={pelaksanaanListData?.meta?.completed ?? 0}
                    maxValue={pelaksanaanListData?.meta?.total ?? 0}
                  />
                )}
              </div>
              <div className="space-y-2 w-full">
                <Text as="h4" variant="heading-md">
                  Pemantauan Kinerja
                </Text>
                <Text color="subdued">
                  Cek Praktik Kinerja, Perilaku Kerja, dan lainnya.
                </Text>
              </div>
              <Link
                href={`${PENGELOLAAN_KINERJA_GPK_TIM_KINERJA_PATH}/pelaksanaan/pemantauan`}
                passHref
              >
                <Button
                  as="a"
                  color="black"
                  className="flex-shrink-0 w-40"
                  disabled={isPelaksanaanListLoading}
                >
                  Lakukan
                  <span className="sr-only">Pemantauan Kinerja</span>
                </Button>
              </Link>
            </div>

            {/* TODO: Uncomment this when the feature is ready */}
            {/* <div className="flex justify-center">
              <Icon>arrow_downward</Icon>
            </div>

            <div className="border border-subdued rounded py-6 pl-6 pr-8 space-x-6 flex items-center">
              <div className="w-20 h-20 flex-shrink-0 flex items-center justify-center">
                <CircularProgress value={0} maxValue={0} />
              </div>
              <div className="space-y-2 w-full">
                <Text as="h4" variant="heading-md">
                  Rekomendasi Predikat Kinerja
                </Text>
                <Text color="subdued">
                  Rekomendasi yang Anda lakukan akan dikirim ke Pejabat Penilai
                  Kinerja.
                </Text>
              </div>
              <Link
                href={`${PENGELOLAAN_KINERJA_GPK_TIM_KINERJA_PATH}/pelaksanaan/daftar`}
                passHref
              >
                <Button
                  as="a"
                  color="black"
                  className="flex-shrink-0 w-40"
                  disabled // TODO: enable later if the feature is ready
                >
                  Lakukan
                  <span className="sr-only">Rekomendasi Predikat Kinerja</span>
                </Button>
              </Link>
            </div> */}
          </SectionCardBody>
        </SectionCard>
      </div>
    </main>
  )
}
